// PalcoDaCapa.js — a coluna da capa na página do livro.
//
// ================= O QUE ELA ERA, E POR QUE MUDOU (17/09/2026) =================
//
// A capa vinha solta: a imagem do tamanho da coluna, numa moldura chapada, e
// nada mais. Numa página que vende um OBJETO — o livro —, o objeto era a peça
// mais pobre da tela, e o dono apontou exatamente essa parte ("o lugar onde
// aparece o livro com os preços e especificações") como a que estava simples.
//
// A resposta foi um PALCO (painel com clarão e chão) — e ele durou um dia: o
// dono viu e pediu só a capa, sem o quadrado em volta. O que ficou é a capa com
// a sombra macia, que era a parte do palco que fazia o trabalho.
//
//  1. A SOMBRA DA CAPA É MACIA, e isso não contradiz o desenho do site. A
//     sombra dura (deslocada, sem desfoque) é a linguagem dos CARTÕES de
//     interface, e o cartão de preço ao lado é o único que a usa no hero — é
//     o que o destaca. A capa não é um cartão; é a foto de um livro, e livro
//     de verdade projeta sombra difusa. Usar a dura aqui empataria os dois
//     objetos, que é o que o comentário antigo desta coluna já evitava.
//
//  2. "LEIA UM TRECHO" MUDOU DE LUGAR, para debaixo da capa. Ele morava entre as
//     estrelas e o cartão de preço, no meio do caminho entre decidir e comprar.
//     Ler um trecho é uma pergunta sobre o LIVRO, e mora junto do livro.
//
//  3. OS FORMATOS À VISTA. "E-book", "Impresso", "Grátis", "E-book de presente":
//     antes, a pessoa só descobria o que existia abrindo as opções do cartão
//     de preço. Aqui elas são ditas em uma linha, antes de qualquer número.
import React from "react";

const API_URL = process.env.REACT_APP_API;

/** Uma plaquinha de formato. `tom` escolhe a cor pelo SIGNIFICADO. */
const Formato = ({ icone, children, tom = "neutro" }) => {
  const tons = {
    neutro: "border-insigne-sombra bg-insigne-navy-soft text-white/80",
    // Verde é "você paga menos" no site inteiro (selo de desconto, economia).
    // Grátis é o extremo da mesma escala.
    gratis: "border-insigne-promo bg-insigne-promo text-ink-fixed",
    // Âmbar é a cor do presente desde a primeira versão do brinde.
    presente: "border-amber-300/40 bg-amber-300/10 text-amber-200",
  };
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-selo border-2 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide " +
        tons[tom]
      }
    >
      <i className={"bi " + icone} aria-hidden="true" />
      {children}
    </span>
  );
};

export default function PalcoDaCapa({
  titulo,
  imagens,
  imgAtiva,
  aoTrocarImagem,
  aoLerTrecho,
  formatos,
}) {
  const principal = imagens[imgAtiva] || imagens[0];

  return (
    <div>
      {/* ===== SÓ A CAPA (18/09/2026) =====
          Aqui havia um PALCO: um painel com borda, clarão laranja por trás e
          um "chão" escuro onde a sombra do livro pousava. O dono olhou e disse
          que não gostou do quadrado em volta — queria só a capa.
          Ele tinha um motivo de desenho (dar profundidade ao objeto que a
          página vende), mas o preço era alto: uma moldura de 2px em volta de
          uma capa que JÁ tem moldura de 2px, e duas bordas concêntricas a 16px
          uma da outra lêem como erro de alinhamento, não como palco.
          Ficou a capa com a sombra macia — a sombra continua fazendo o livro
          parecer apoiado, e é ela que dava a profundidade de verdade. */}
      {principal && (
        <img
          src={`${API_URL}public/${principal}`}
          alt={titulo}
          className="mx-auto block w-full max-w-[220px] rounded-card border-2 border-insigne-sombra
                     object-cover shadow-[0_24px_36px_-14px_rgba(0,0,0,0.6),0_8px_14px_-8px_rgba(0,0,0,0.45)]
                     sm:max-w-none"
        />
      )}

      {/* Miniaturas, só quando existe mais de uma imagem. */}
      {imagens.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {imagens.slice(0, 4).map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => aoTrocarImagem(i)}
              aria-label={`Ver imagem ${i + 1}`}
              aria-pressed={imgAtiva === i}
              className={
                "foco-laranja h-20 w-16 overflow-hidden rounded-card border-2 transition-colors " +
                (imgAtiva === i ? "border-insigne-orange" : "border-insigne-sombra hover:border-white/30")
              }
            >
              <img src={`${API_URL}public/${img}`} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
          {imagens.length > 4 && (
            <div className="flex h-20 w-16 items-center justify-center rounded-card border-2 border-insigne-sombra text-sm font-semibold text-white/60">
              +{imagens.length - 4}
            </div>
          )}
        </div>
      )}

      {/* SÓ DO DESKTOP PARA CIMA, os formatos e o "Leia um trecho" (17/09/2026).
          No celular a coluna da capa vem ANTES do título, e o print de 390px
          mostrou as duas coisas empurrando o nome do livro para fora da
          primeira tela. Lá os formatos repetiriam os ladrilhos do cartão de
          preço, que já mostram os dois; e o trecho é oferecido logo depois do
          cartão (ver PaginaProduto), onde ele não compete com o comprar. */}
      {formatos.length > 0 && (
        <div className="mt-4 hidden flex-wrap gap-2 lg:flex" aria-label="Formatos disponíveis">
          {formatos.map((f) => (
            <Formato key={f.chave} icone={f.icone} tom={f.tom}>
              {f.texto}
            </Formato>
          ))}
        </div>
      )}

      {aoLerTrecho && (
        <button
          type="button"
          onClick={aoLerTrecho}
          // Continua VAZADO: o único botão cheio do hero é o de comprar, e um
          // segundo laranja sólido aqui dividiria a atenção entre os dois.
          className="foco-laranja mt-4 hidden w-full items-center justify-center gap-2 rounded-controle border-2 lg:flex
                     border-insigne-orange px-5 py-2.5 text-sm font-semibold text-insigne-orange
                     transition-colors hover:bg-insigne-orange hover:text-paper"
        >
          <i className="bi bi-book-half" aria-hidden="true" /> Leia um trecho
        </button>
      )}
    </div>
  );
}
